import { useMemo, useState, useEffect } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Button from 'domains/core/components/Button';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';

type Props = {
    isLoading: boolean;
    onSaveAndExit: (status?: ContentStatus) => void;
    content: string;
};

const SaveAndExitButton = ({ isLoading, onSaveAndExit, content }: Props) => {
    const { contentDraft: hasContentDraft } = useFlags();
    const [currentAction, setCurrentAction] = useState<'saving' | 'publishing' | null>(null);
    const isMessageInvalidLength = !content || content.trim().length < 3 || content.length > 1600;

    // Reset currentAction when loading is complete
    useEffect(() => {
        if (!isLoading) {
            setCurrentAction(null);
        }
    }, [isLoading]);

    const handleSaveAndExitClick = () => {
        setCurrentAction('saving');
        onSaveAndExit(ContentStatus.DRAFT);
    };

    const handlePublishClick = () => {
        setCurrentAction('publishing');
        onSaveAndExit(ContentStatus.PUBLISHED);
    };

    const tooltipTitle = useMemo(() => {
        let tooltipTitle = '';
        if (isLoading && currentAction === 'publishing') {
            tooltipTitle = tooltipTitle + 'Content is publishing. ';
        } else if (isLoading && currentAction === 'saving') {
            tooltipTitle = tooltipTitle + 'Content is saving. ';
        } else if (isLoading) {
            tooltipTitle = tooltipTitle + 'Content is loading. ';
        }
        if (isMessageInvalidLength) {
            tooltipTitle = tooltipTitle + 'Message must be between 3 and 1600 characters. ';
        }
        return tooltipTitle;
    }, [isLoading, isMessageInvalidLength, currentAction]);

    return (
        <Tooltip title={tooltipTitle}>
            <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                <Button
                    disabled={isLoading || isMessageInvalidLength}
                    onClick={handleSaveAndExitClick}
                    variant="outlined"
                    data-testid="save-exit-button"
                    role="button"
                >
                    Save & Exit
                </Button>
                {hasContentDraft && (
                    <Button
                        disabled={isLoading || isMessageInvalidLength}
                        onClick={handlePublishClick}
                        variant="contained"
                        data-testid="publish-button"
                        role="button"
                    >
                        Publish
                    </Button>
                )}
            </Stack>
        </Tooltip>
    );
};

export default SaveAndExitButton;
